import { ReactNode, useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import AccountSwitcher from './AccountSwitcher';
import NotificationBell from './NotificationBell';

const DRAWER_WIDTH_OPEN = 200;
const DRAWER_WIDTH_CLOSED = 56;

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() || user.email[0].toUpperCase()
    : '';

  const drawerWidth = drawerOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED;

  // Unread alerts on the sidebar item; refreshed on every page change and every 30s.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user?.is_superuser) return;
    const fetch = () => api.get('/notifications/unread-count/').then((r) => setUnread(r.data.count)).catch(() => {});
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, [user, location.pathname]);

  const reports = { label: 'Reports', icon: <DescriptionOutlinedIcon />, path: '/' };
  const settings = { label: 'Settings', icon: <SettingsOutlinedIcon />, path: '/settings' };
  const alerts = {
    label: 'Alerts',
    icon: <Badge badgeContent={unread} color="error"><NotificationsNoneOutlinedIcon /></Badge>,
    path: '/alerts',
  };
  const clients = { label: 'Clients', icon: <PersonAddAltOutlinedIcon />, path: '/clients' };
  const accounts = { label: 'Accounts', icon: <AdminPanelSettingsOutlinedIcon />, path: '/accounts' };
  // Clients get no Alerts item; their notifications come through the header bell.
  const navItems = user?.is_superuser ? [clients, accounts, alerts, reports, settings] : [reports, settings];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      {/* Top bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ minHeight: '52px !important' }}>
          <IconButton aria-label="Menu"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{ color: '#6B7280', '&:hover': { color: '#102B3D' }, mr: 1.5, ml: -0.5 }}
            size="small"
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: '1.1rem',
                cursor: 'pointer',
                letterSpacing: '-0.01em',
              }}
              onClick={() => navigate('/')}
            >
              <span style={{ color: '#102B3D' }}>Full</span>
              <span style={{ color: '#10A4C2' }}>Financials</span>
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <AccountSwitcher />
              </Box>
              {user.is_superuser && (
                <Chip
                  label="Admin"
                  size="small"
                  sx={{
                    bgcolor: '#E8F8FB', color: '#0D8DA7', fontWeight: 600,
                    fontSize: '0.7rem', height: 22, border: '1px solid #10A4C2',
                  }}
                />
              )}
              {/* Admins read alerts on the Alerts page; clients keep the bell. */}
              {!user.is_superuser && <NotificationBell />}
              <Tooltip title={`${user.first_name} ${user.last_name}`.trim() || user.email}>
                <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ p: 0, ml: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 30, height: 30, fontSize: '0.75rem', fontWeight: 700,
                      bgcolor: '#102B3D', color: '#fff',
                    }}
                  >
                    {initials}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={() => setUserMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 220, mt: 0.5, borderRadius: 2 } } }}
              >
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: '#102B3D' }}>
                    {`${user.first_name} ${user.last_name}`.trim() || user.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    {user.email}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem
                  onClick={() => { setUserMenuAnchor(null); handleLogout(); }}
                  sx={{ gap: 1.5, py: 1, fontSize: '0.85rem', color: '#6B7280' }}
                >
                  <LogoutIcon fontSize="small" />
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Left sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.2s',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            transition: 'width 0.2s',
            overflowX: 'hidden',
            bgcolor: '#EEF7F9',
            borderRight: '1px solid #D7E1E8',
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
              `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'>` +
              `<path d='M0 18L12 6L24 18' fill='none' stroke='rgba(16,164,194,0.12)' stroke-width='1'/>` +
              `<path d='M0 21L12 9L24 21' fill='none' stroke='rgba(16,164,194,0.08)' stroke-width='0.75'/>` +
              `<path d='M0 24L12 12L24 24' fill='none' stroke='rgba(16,164,194,0.05)' stroke-width='0.5'/>` +
              `</svg>`
            )}")`,
          },
        }}
      >
        <Toolbar sx={{ minHeight: '52px !important' }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <List sx={{ px: 0.5, pt: 1 }}>
            {navItems.map((item) => {
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

              const button = (
                <ListItemButton
                  key={item.label}
                  onClick={() => navigate(item.path)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    minHeight: 40,
                    px: drawerOpen ? 1.5 : 1,
                    justifyContent: drawerOpen ? 'initial' : 'center',
                    bgcolor: isActive ? 'rgba(16, 164, 194, 0.12)' : 'transparent',
                    '&:hover': { bgcolor: isActive ? 'rgba(16, 164, 194, 0.16)' : 'rgba(16,164,194,0.06)' },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: drawerOpen ? 1.5 : 0,
                      justifyContent: 'center',
                      color: isActive ? '#10A4C2' : '#6B7280',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {drawerOpen && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? '#102B3D' : '#374151',
                      }}
                    />
                  )}
                </ListItemButton>
              );

              return drawerOpen ? button : (
                <Tooltip key={item.label} title={item.label} placement="right" arrow>
                  {button}
                </Tooltip>
              );
            })}
          </List>

          <Box sx={{ flexGrow: 1 }} />

        </Box>
      </Drawer>

      {/* Main content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, minWidth: 0 }}>
        <Toolbar sx={{ minHeight: '52px !important' }} />
        {children}
      </Box>
    </Box>
  );
}
